import type { BackgroundAsset, BackgroundReference } from "./stateTypes";
import type {
  ArcShape,
  BaseEntityMeta,
  CanvasIconEntity,
  CanvasPoint,
  CircleHole,
  CircleShape,
  Dimension,
  PolylineLikeShape,
  Shape,
  TextEntity,
} from "./types";

export const CANVAS_OBJECT_STORAGE_SCHEMA_VERSION = 1 as const;

export type CanvasObjectStorageSchemaVersion = typeof CANVAS_OBJECT_STORAGE_SCHEMA_VERSION;

export type StoredBackgroundAsset = {
  kind: BackgroundAsset["kind"];
  name: string;
  center: CanvasPoint;
  width: number;
  height: number;
  rotation: number;
  reference: BackgroundReference | null;
};

export type StoredPolylineObject = BaseEntityMeta & {
  objectType: "shape";
  kind: "polyline" | "rectangle";
  points: number[];
  holes?: number[][];
  circleHoles?: CircleHole[];
  closed: boolean;
  hatchPattern?: PolylineLikeShape["hatchPattern"];
  hatchScale?: number;
  showVertexMarkersOnly?: boolean;
  lifeLineVertices?: PolylineLikeShape["lifeLineVertices"];
  lifeLineDesignPoints?: CanvasPoint[];
  lifeLineLineNumber?: number;
};

export type StoredCircleObject = BaseEntityMeta & {
  objectType: "shape";
  kind: "circle";
  closed: true;
  center: CanvasPoint;
  radius: number;
};

export type StoredArcObject = BaseEntityMeta & {
  objectType: "shape";
  kind: "arc";
  closed: false;
  center: CanvasPoint;
  radius: number;
  startAngle: number;
  endAngle: number;
};

export type StoredShapeObject = StoredPolylineObject | StoredCircleObject | StoredArcObject;

export type StoredDimensionObject = BaseEntityMeta & {
  objectType: "dimension";
  start: CanvasPoint;
  end: CanvasPoint;
  offset: number;
  dimensionType?: Dimension["dimensionType"];
};

export type StoredTextObject = BaseEntityMeta & {
  objectType: "text";
  kind: "text";
  position: CanvasPoint;
  text: string;
  height: number;
  boxWidth?: number;
  boxHeight?: number;
  fontFamily: string;
  justification: TextEntity["justification"];
};

export type StoredIconObject = BaseEntityMeta & {
  objectType: "icon";
  kind: "icon";
  iconId: string;
  position: CanvasPoint;
  size: number;
  mainColor: string;
  rotation?: number;
};

export type StoredCanvasObject = StoredShapeObject | StoredDimensionObject | StoredTextObject | StoredIconObject;

export type CanvasObjectStoragePayload = {
  schemaVersion: CanvasObjectStorageSchemaVersion;
  configurationId?: string;
  generatedAt: string;
  objects: StoredCanvasObject[];
  backgroundAsset: StoredBackgroundAsset | null;
  backgroundTransparency: number;
};

export type CreateCanvasObjectStoragePayloadInput = {
  configurationId?: string;
  shapes: Shape[];
  dimensions: Dimension[];
  textEntities?: TextEntity[];
  iconEntities?: CanvasIconEntity[];
  backgroundAsset: BackgroundAsset | null;
  backgroundTransparency: number;
  generatedAt?: string;
};

export type RestoredCanvasObjects = {
  shapes: Shape[];
  dimensions: Dimension[];
  textEntities: TextEntity[];
  iconEntities: CanvasIconEntity[];
  backgroundAsset: StoredBackgroundAsset | null;
  backgroundTransparency: number;
};

const clonePoint = (point: CanvasPoint): CanvasPoint => ({
  x: point.x,
  y: point.y,
});

const cloneCircleHole = (hole: CircleHole): CircleHole => ({
  center: clonePoint(hole.center),
  radius: hole.radius,
});

const cloneBackgroundReference = (reference: BackgroundReference | null): BackgroundReference | null =>
  reference
    ? {
        startLocal: clonePoint(reference.startLocal),
        endLocal: clonePoint(reference.endLocal),
        realLength: reference.realLength,
      }
    : null;

const cloneBackgroundAssetForStorage = (asset: BackgroundAsset | null): StoredBackgroundAsset | null =>
  asset
    ? {
        kind: asset.kind,
        name: asset.name,
        center: clonePoint(asset.center),
        width: asset.width,
        height: asset.height,
        rotation: asset.rotation,
        reference: cloneBackgroundReference(asset.reference),
      }
    : null;

const cloneBaseEntityMeta = <T extends BaseEntityMeta>(entity: T): BaseEntityMeta => ({
  id: entity.id,
  name: entity.name,
  strokeColor: entity.strokeColor,
  strokeWidth: entity.strokeWidth,
  layer: entity.layer,
  entityStyleKind: entity.entityStyleKind,
  roofGroupId: entity.roofGroupId,
  roofGroupName: entity.roofGroupName,
  roofGroupCategory: entity.roofGroupCategory,
  roofTypeId: entity.roofTypeId,
  roofTypeName: entity.roofTypeName,
  roofPitch: entity.roofPitch,
  roofMaterialId: entity.roofMaterialId,
  roofMaterialName: entity.roofMaterialName,
  roofSubstructureId: entity.roofSubstructureId,
  roofSubstructureName: entity.roofSubstructureName,
  roofSubstructureHeight: entity.roofSubstructureHeight,
  roofSubstructureSizeHeight: entity.roofSubstructureSizeHeight,
  roofSubstructureSizeWidth: entity.roofSubstructureSizeWidth,
  roofMetalPlateThickness: entity.roofMetalPlateThickness,
  roofSheetMaterialId: entity.roofSheetMaterialId,
  roofSheetMaterialName: entity.roofSheetMaterialName,
  roofUseOnlyAnchorPoints: entity.roofUseOnlyAnchorPoints,
  roofSystemId: entity.roofSystemId,
  roofSystemName: entity.roofSystemName,
  roofNormativeId: entity.roofNormativeId,
  roofNormativeName: entity.roofNormativeName,
  roofSeamsSpan: entity.roofSeamsSpan,
  roofInsulationThickness: entity.roofInsulationThickness,
  roofWaveSpan: entity.roofWaveSpan,
  roofHipsSpan: entity.roofHipsSpan,
  roofSupportMaterialId: entity.roofSupportMaterialId,
  roofSupportMaterialName: entity.roofSupportMaterialName,
  roofTerminalCurveSupportId: entity.roofTerminalCurveSupportId,
  roofTerminalCurveSupportCode: entity.roofTerminalCurveSupportCode,
  roofTerminalCurveSupportName: entity.roofTerminalCurveSupportName,
  roofIntermediateSupportId: entity.roofIntermediateSupportId,
  roofIntermediateSupportCode: entity.roofIntermediateSupportCode,
  roofIntermediateSupportName: entity.roofIntermediateSupportName,
  roofAnchorPointMaterialId: entity.roofAnchorPointMaterialId,
  roofAnchorPointMaterialName: entity.roofAnchorPointMaterialName,
  roofAnchorPointMappingId: entity.roofAnchorPointMappingId,
  roofAnchorPointCode: entity.roofAnchorPointCode,
  roofAnchorPointName: entity.roofAnchorPointName,
  roofComponentMaterialId: entity.roofComponentMaterialId,
  roofComponentMaterialName: entity.roofComponentMaterialName,
  roofComponentAbsorberId: entity.roofComponentAbsorberId,
  roofComponentAbsorberCode: entity.roofComponentAbsorberCode,
  roofComponentAbsorberName: entity.roofComponentAbsorberName,
  roofComponentSystemTypeId: entity.roofComponentSystemTypeId,
  roofComponentSystemTypeName: entity.roofComponentSystemTypeName,
  roofFixed: entity.roofFixed,
  roofShuttleId: entity.roofShuttleId,
  roofShuttleCode: entity.roofShuttleCode,
  roofShuttleName: entity.roofShuttleName,
  roofNrOfShuttles: entity.roofNrOfShuttles,
  roofMembraneId: entity.roofMembraneId,
  roofMembraneName: entity.roofMembraneName,
  safetySystemId: entity.safetySystemId,
  safetySystemName: entity.safetySystemName,
  anchorPointAdditionalProductCodes: entity.anchorPointAdditionalProductCodes,
  anchorPointOptionalProductCodes: entity.anchorPointOptionalProductCodes,
  isObstacle: entity.isObstacle,
  fallSide: entity.fallSide,
  disableFill: entity.disableFill,
});

export const serializeShapeForStorage = (shape: Shape): StoredShapeObject => {
  const base = cloneBaseEntityMeta(shape);

  if (shape.kind === "circle") {
    return {
      ...base,
      objectType: "shape",
      kind: "circle",
      closed: true,
      center: clonePoint(shape.center),
      radius: shape.radius,
    };
  }

  if (shape.kind === "arc") {
    return {
      ...base,
      objectType: "shape",
      kind: "arc",
      closed: false,
      center: clonePoint(shape.center),
      radius: shape.radius,
      startAngle: shape.startAngle,
      endAngle: shape.endAngle,
    };
  }

  return {
    ...base,
    objectType: "shape",
    kind: shape.kind,
    points: [...shape.points],
    holes: shape.holes?.map((hole) => [...hole]),
    circleHoles: shape.circleHoles?.map(cloneCircleHole),
    closed: shape.closed,
    hatchPattern: shape.hatchPattern,
    hatchScale: shape.hatchScale,
    showVertexMarkersOnly: shape.showVertexMarkersOnly,
    lifeLineVertices: shape.lifeLineVertices?.map((vertex) => ({ ...vertex })),
    lifeLineDesignPoints: shape.lifeLineDesignPoints?.map(clonePoint),
    lifeLineLineNumber: shape.lifeLineLineNumber,
  };
};

export const serializeDimensionForStorage = (dimension: Dimension): StoredDimensionObject => ({
  ...cloneBaseEntityMeta(dimension),
  objectType: "dimension",
  start: clonePoint(dimension.start),
  end: clonePoint(dimension.end),
  offset: dimension.offset,
  dimensionType: dimension.dimensionType,
});

export const serializeTextForStorage = (textEntity: TextEntity): StoredTextObject => ({
  ...cloneBaseEntityMeta(textEntity),
  objectType: "text",
  kind: "text",
  position: clonePoint(textEntity.position),
  text: textEntity.text,
  height: textEntity.height,
  boxWidth: textEntity.boxWidth,
  boxHeight: textEntity.boxHeight,
  fontFamily: textEntity.fontFamily,
  justification: textEntity.justification,
});

export const serializeIconForStorage = (iconEntity: CanvasIconEntity): StoredIconObject => ({
  ...cloneBaseEntityMeta(iconEntity),
  objectType: "icon",
  kind: "icon",
  iconId: iconEntity.iconId,
  position: clonePoint(iconEntity.position),
  size: iconEntity.size,
  mainColor: iconEntity.mainColor,
  rotation: iconEntity.rotation,
});

export const createCanvasObjectStoragePayload = ({
  configurationId,
  shapes,
  dimensions,
  textEntities = [],
  iconEntities = [],
  backgroundAsset,
  backgroundTransparency,
  generatedAt = new Date().toISOString(),
}: CreateCanvasObjectStoragePayloadInput): CanvasObjectStoragePayload => ({
  schemaVersion: CANVAS_OBJECT_STORAGE_SCHEMA_VERSION,
  configurationId,
  generatedAt,
  objects: [
    ...shapes.map(serializeShapeForStorage),
    ...dimensions.map(serializeDimensionForStorage),
    ...textEntities.map(serializeTextForStorage),
    ...iconEntities.map(serializeIconForStorage),
  ],
  backgroundAsset: cloneBackgroundAssetForStorage(backgroundAsset),
  backgroundTransparency,
});

export const restoreShapeFromStorage = (object: StoredShapeObject): Shape => {
  if (object.kind === "circle") {
    const { objectType: _objectType, ...shape } = object;

    return {
      ...shape,
      center: clonePoint(shape.center),
    } satisfies CircleShape;
  }

  if (object.kind === "arc") {
    const { objectType: _objectType, ...shape } = object;

    return {
      ...shape,
      center: clonePoint(shape.center),
    } satisfies ArcShape;
  }

  const { objectType: _objectType, ...shape } = object;

  return {
    ...shape,
    points: [...shape.points],
    holes: shape.holes?.map((hole) => [...hole]),
    circleHoles: shape.circleHoles?.map(cloneCircleHole),
    hatchPattern: shape.hatchPattern,
    hatchScale: shape.hatchScale,
    lifeLineVertices: shape.lifeLineVertices?.map((vertex) => ({ ...vertex })),
    lifeLineDesignPoints: shape.lifeLineDesignPoints?.map(clonePoint),
  } satisfies PolylineLikeShape;
};

export const restoreDimensionFromStorage = (object: StoredDimensionObject): Dimension => {
  const { objectType: _objectType, ...dimension } = object;

  return {
    ...dimension,
    start: clonePoint(dimension.start),
    end: clonePoint(dimension.end),
  };
};

export const restoreTextFromStorage = (object: StoredTextObject): TextEntity => {
  const { objectType: _objectType, ...textEntity } = object;

  return {
    ...textEntity,
    position: clonePoint(textEntity.position),
    boxWidth:
      typeof textEntity.boxWidth === "number" && Number.isFinite(textEntity.boxWidth)
        ? Math.max(20, textEntity.boxWidth)
        : Math.max(80, textEntity.text.length * textEntity.height * 0.6 + 12),
    boxHeight:
      typeof textEntity.boxHeight === "number" && Number.isFinite(textEntity.boxHeight)
        ? Math.max(20, textEntity.boxHeight)
        : Math.max(32, textEntity.height + 12),
  };
};

export const restoreIconFromStorage = (object: StoredIconObject): CanvasIconEntity => {
  const { objectType: _objectType, ...iconEntity } = object;

  return {
    ...iconEntity,
    position: clonePoint(iconEntity.position),
    size:
      typeof iconEntity.size === "number" && Number.isFinite(iconEntity.size)
        ? Math.max(8, iconEntity.size)
        : 48,
    mainColor: iconEntity.mainColor || iconEntity.strokeColor,
    rotation:
      typeof iconEntity.rotation === "number" && Number.isFinite(iconEntity.rotation) ? iconEntity.rotation : 0,
  };
};

export const restoreCanvasObjectsFromPayload = (
  payload: CanvasObjectStoragePayload,
): RestoredCanvasObjects => ({
  shapes: payload.objects.flatMap((object) =>
    object.objectType === "shape" ? [restoreShapeFromStorage(object)] : [],
  ),
  dimensions: payload.objects.flatMap((object) =>
    object.objectType === "dimension" ? [restoreDimensionFromStorage(object)] : [],
  ),
  textEntities: payload.objects.flatMap((object) =>
    object.objectType === "text" ? [restoreTextFromStorage(object)] : [],
  ),
  iconEntities: payload.objects.flatMap((object) =>
    object.objectType === "icon" ? [restoreIconFromStorage(object)] : [],
  ),
  backgroundAsset: payload.backgroundAsset
    ? {
        ...payload.backgroundAsset,
        center: clonePoint(payload.backgroundAsset.center),
        reference: cloneBackgroundReference(payload.backgroundAsset.reference),
      }
    : null,
  backgroundTransparency: payload.backgroundTransparency,
});
